import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../amplify-client';
import { GuennieRating } from '../components/GuennieRating';
import { SpaghettiDivider } from '../components/SpaghettiDivider';

type Kartenpunkt = {
  id: string;
  name: string;
  ort: string;
  anzahlTests: number;
  avgGesamtGuennies?: number | null;
  avgSpaghettiOptik?: number | null;
  avgFruchtsauce?: number | null;
  avgPreisLeistung?: number | null;
};

const KATEGORIEN = [
  { feld: 'avgGesamtGuennies', label: 'Meiste Günnies' },
  { feld: 'avgSpaghettiOptik', label: 'Beste Spaghetti-Optik' },
  { feld: 'avgFruchtsauce', label: 'Beste Fruchtsauce' },
  { feld: 'avgPreisLeistung', label: 'Bestes Preis-Leistungs-Verhältnis' },
] as const;

type KategorieFeld = (typeof KATEGORIEN)[number]['feld'];

const TABELLENPLATZ_LABEL = ['🥇', '🥈', '🥉'];

/**
 * „Empfehlungen der Redaktion" (Bauphase 7, siehe technical-design.md 4.3 /
 * 13.8): pro Kategorie eine Rangliste. Alle vier Werte kommen bereits fertig
 * berechnet aus `karteEisdielen` (vom eisdiele-aggregation-Lambda gepflegt) —
 * hier wird nur noch sortiert und gefiltert, keine eigene Serverlogik nötig.
 */
export function Bestenlisten() {
  const [punkte, setPunkte] = useState<Kartenpunkt[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kategorie, setKategorie] = useState<KategorieFeld>('avgGesamtGuennies');

  useEffect(() => {
    client.queries
      .karteEisdielen()
      .then(({ data, errors }) => {
        if (errors?.length) throw new Error(errors[0].message);
        setPunkte((data ?? []).filter(Boolean) as Kartenpunkt[]);
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, []);

  const rangliste = useMemo(
    () =>
      punkte
        .filter((p) => typeof p[kategorie] === 'number')
        .sort((a, b) => (b[kategorie] as number) - (a[kategorie] as number)),
    [punkte, kategorie],
  );

  return (
    <div className="container">
      <p className="label">Die Empfehlungen der Redaktion</p>
      <h1>Bestenlisten</h1>
      <SpaghettiDivider />

      <nav style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {KATEGORIEN.map((k) => (
          <button
            key={k.feld}
            type="button"
            className={kategorie === k.feld ? 'btn btn-primaer' : 'btn btn-sekundaer'}
            onClick={() => setKategorie(k.feld)}
          >
            {k.label}
          </button>
        ))}
      </nav>

      {laedt ? <p className="label">Lädt …</p> : null}
      {fehler ? (
        <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
          Konnte die Bestenliste nicht laden: {fehler}
        </p>
      ) : null}
      {!laedt && !fehler && rangliste.length === 0 ? (
        <p style={{ color: 'var(--espresso-70)' }}>
          Noch keine Eisdiele mit Wert in dieser Kategorie. Ein Auftrag für die Redaktion.
        </p>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
        {rangliste.map((p, i) => (
          <Link
            key={p.id}
            to={`/app/eisdielen/${p.id}`}
            className="karte"
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <span
              className="tabellenziffern"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.2rem',
                width: '2.2rem',
                flexShrink: 0,
                color: i < 3 ? 'inherit' : 'var(--espresso-70)',
              }}
            >
              {TABELLENPLATZ_LABEL[i] ?? i + 1}
            </span>
            <div style={{ flex: 1, minWidth: '10rem' }}>
              <strong style={{ fontFamily: 'var(--font-display)' }}>{p.name}</strong>
              <div style={{ color: 'var(--espresso-70)', fontSize: '0.9rem' }}>
                {p.ort} · {p.anzahlTests} Verkostung{p.anzahlTests === 1 ? '' : 'en'}
              </div>
            </div>
            <GuennieRating wert={p[kategorie]} groesse="sm" />
          </Link>
        ))}
      </div>
    </div>
  );
}
